import { useEffect, useId, useRef, useState } from 'react';
import { CalendarDays, Check, Download } from 'lucide-react';
import { SelectableChip } from '../forms/SelectableChip';
import { Button } from '../actions/Button';
import {
  CALENDAR_PERIODS,
  customRange,
  customRangeDays,
  isCustomRange,
  parseTypedDay,
  shortDay,
  type CalendarPeriod,
  type CustomRange,
  type DayRange,
  type RangePreset,
} from '../../lib/format/dates';

/** A range is a preset chip, a calendar period from the popover, or a custom `start..end`. */
export type RangeValue = RangePreset | CalendarPeriod | CustomRange;

const PRESETS: Array<[RangePreset, string]> = [
  ['7d', '7d'],
  ['30d', '30d'],
  ['90d', '90d'],
  ['all', 'All'],
];

export function isCalendarPeriod(value: string): value is CalendarPeriod {
  return CALENDAR_PERIODS.some(([key]) => key === value);
}

/** The calendar chip's label for a period or custom range; undefined for a preset. */
export function calendarChipLabel(value: RangeValue): string | undefined {
  if (isCustomRange(value)) {
    const { start, end } = customRangeDays(value);
    return `${shortDay(start!)} - ${shortDay(end!)}`;
  }
  return CALENDAR_PERIODS.find(([key]) => key === value)?.[1];
}

/**
 * The standard date-range control — 7d / 30d / 90d / All chips plus a calendar
 * chip. The calendar opens a menu of calendar periods (last 30 days / 12
 * months, month / quarter / year to date, last month / quarter / year) and
 * "Date range", which swaps the menu for Start / End fields (type a date or
 * pick one) and Apply. The chosen period or range becomes the calendar chip's
 * label ("Last month", "09/20/26 - 09/21/26").
 */
export function RangePicker({ value, onChange }: { value: RangeValue; onChange: (value: RangeValue) => void }) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<'periods' | 'custom'>('periods');
  const wrapRef = useRef<HTMLDivElement>(null);
  const menuId = useId();
  const custom = isCustomRange(value);
  const calendarActive = isCalendarPeriod(value) || custom;
  const calendarLabel = calendarChipLabel(value);

  const close = () => {
    setOpen(false);
    setMode('periods');
  };

  useEffect(() => {
    if (!open) return;
    const shut = () => {
      setOpen(false);
      setMode('periods');
    };
    const onDown = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) shut();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        shut();
      }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey, true);
    };
  }, [open]);

  const item =
    'flex w-full items-center gap-2 border-0 bg-transparent px-3 py-[7px] text-left text-sm text-ink hover:bg-[rgba(143,68,32,.06)]';

  return (
    <div className="flex gap-1.5" role="group" aria-label="Date range">
      {PRESETS.map(([key, label]) => (
        <SelectableChip key={key} active={value === key} onClick={() => onChange(key)}>
          {label}
        </SelectableChip>
      ))}
      <div ref={wrapRef} className="relative">
        <SelectableChip
          active={calendarActive}
          aria-haspopup="menu"
          aria-expanded={open}
          aria-controls={open ? menuId : undefined}
          aria-label={calendarLabel ? `Calendar period: ${calendarLabel}` : 'Calendar period'}
          icon={<CalendarDays size={15} aria-hidden="true" />}
          onClick={() => (open ? close() : setOpen(true))}
        >
          {calendarLabel}
        </SelectableChip>
        {open && mode === 'periods' && (
          <div
            id={menuId}
            role="menu"
            aria-label="Calendar periods"
            className="absolute left-0 top-full z-50 mt-1.5 min-w-[190px] rounded-app border border-panel-border bg-white py-1.5 font-body shadow-lg"
          >
            {CALENDAR_PERIODS.map(([key, label]) => {
              const checked = value === key;
              return (
                <button
                  key={key}
                  type="button"
                  role="menuitemradio"
                  aria-checked={checked}
                  onClick={() => {
                    onChange(key);
                    close();
                  }}
                  className={item}
                >
                  <span className="flex w-4 shrink-0 justify-center text-brown">
                    {checked && <Check size={15} aria-hidden="true" />}
                  </span>
                  {label}
                </button>
              );
            })}
            <button
              type="button"
              role="menuitemradio"
              aria-checked={custom}
              onClick={() => setMode('custom')}
              className={item}
            >
              <span className="flex w-4 shrink-0 justify-center text-brown">
                {custom && <Check size={15} aria-hidden="true" />}
              </span>
              Date range
            </button>
          </div>
        )}
        {open && mode === 'custom' && (
          <CustomRangeForm
            id={menuId}
            initial={custom ? customRangeDays(value) : {}}
            onApply={(start, end) => {
              onChange(customRange(start, end));
              close();
            }}
          />
        )}
      </div>
    </div>
  );
}

/** The calendar menu's "Date range" view: Start and End (typed or picked) and Apply. */
function CustomRangeForm({
  id,
  initial,
  onApply,
}: {
  id: string;
  initial: DayRange;
  onApply: (start: string, end: string) => void;
}) {
  const [start, setStart] = useState(initial.start ? shortDay(initial.start) : '');
  const [end, setEnd] = useState(initial.end ? shortDay(initial.end) : '');
  const startDay = parseTypedDay(start);
  const endDay = parseTypedDay(end);
  const error =
    (start && !startDay) || (end && !endDay)
      ? 'Use MM/DD/YY.'
      : startDay && endDay && startDay > endDay
        ? 'Start must be on or before End.'
        : undefined;
  const ready = !!startDay && !!endDay && !error;

  return (
    <div
      id={id}
      role="dialog"
      aria-label="Date range"
      className="absolute left-0 top-full z-50 mt-1.5 w-[248px] rounded-app border border-panel-border bg-white p-3 font-body shadow-lg"
    >
      <div className="grid gap-2.5">
        <DayField label="Start" value={start} onChange={setStart} />
        <DayField label="End" value={end} onChange={setEnd} />
        {error && <div className="text-xs text-danger">{error}</div>}
        <Button size="sm" disabled={!ready} onClick={() => ready && onApply(startDay!, endDay!)}>
          Apply
        </Button>
      </div>
    </div>
  );
}

/** A day typed as MM/DD/YY, with a calendar button that opens the browser's date picker. */
function DayField({ label, value, onChange }: { label: string; value: string; onChange: (value: string) => void }) {
  const id = useId();
  const pickerRef = useRef<HTMLInputElement>(null);
  const picked = parseTypedDay(value);
  return (
    <div className="flex items-center gap-2">
      <label htmlFor={id} className="w-10 shrink-0 text-[13px] font-semibold text-muted">
        {label}
      </label>
      <div className="relative flex min-w-0 flex-1 items-center">
        <input
          id={id}
          type="text"
          inputMode="numeric"
          placeholder="MM/DD/YY"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full rounded-input border border-[rgba(143,68,32,.12)] bg-white py-[7px] pl-3 pr-9 font-body text-sm text-ink focus:border-orange focus:outline focus:outline-2 focus:outline-offset-2 focus:outline-orange"
        />
        <button
          type="button"
          aria-label={`Pick the ${label.toLowerCase()} date`}
          onClick={() => pickerRef.current?.showPicker?.()}
          className="absolute right-1.5 flex h-7 w-7 cursor-pointer items-center justify-center rounded-app border-0 bg-transparent p-0 text-muted hover:text-orange"
        >
          <CalendarDays size={15} aria-hidden="true" />
        </button>
        {/* The browser's picker, opened by the button; its choice fills the text field. */}
        <input
          ref={pickerRef}
          type="date"
          tabIndex={-1}
          aria-hidden="true"
          value={picked ?? ''}
          onChange={(e) => e.target.value && onChange(shortDay(e.target.value))}
          className="pointer-events-none absolute bottom-0 right-0 h-0 w-0 opacity-0"
        />
      </div>
    </div>
  );
}

/** Day / Week / Month chips — the series grain (`bucket`). */
export type Grain = 'day' | 'week' | 'month';
const GRAINS: Array<[Grain, string]> = [
  ['day', 'Day'],
  ['week', 'Week'],
  ['month', 'Month'],
];

export function GrainPicker({ value, onChange }: { value: Grain; onChange: (value: Grain) => void }) {
  return (
    <div className="flex items-center gap-1.5" role="group" aria-label="Grain">
      <span className="text-xs text-muted">Grain</span>
      {GRAINS.map(([key, label]) => (
        <SelectableChip key={key} active={value === key} onClick={() => onChange(key)}>
          {label}
        </SelectableChip>
      ))}
    </div>
  );
}

/**
 * Kit `ExportButton` — ghost-dark sm "Export CSV". Takes an async handler
 * (usually lib/api/download `downloadExport`) and shows progress while the
 * BE streams the file.
 */
export function ExportButton({ onExport, label = 'Export CSV' }: { onExport: () => Promise<void>; label?: string }) {
  const [busy, setBusy] = useState(false);
  return (
    <Button
      variant="ghost-dark"
      size="sm"
      loading={busy}
      onClick={async () => {
        setBusy(true);
        try {
          await onExport();
        } finally {
          setBusy(false);
        }
      }}
    >
      <Download size={15} strokeWidth={2} aria-hidden="true" />
      {busy ? 'Exporting…' : label}
    </Button>
  );
}

/**
 * Custom inclusive day range (FE plan §3.8). Always calendar days; the
 * service converts to the endpoint's convention (lib/format/dates
 * `toApiRange`). `timeZoneLabel` states which clock the days are in — the
 * admin profile's timezone for `startDate/endDate` endpoints, UTC for
 * `from/to` ones — so an operator knows where "today" ends.
 */
export function DateRangeFilter({
  value,
  onChange,
  timeZoneLabel,
}: {
  value: DayRange;
  onChange: (value: DayRange) => void;
  timeZoneLabel?: string;
}) {
  const id = useId();
  const input =
    'rounded-input border border-[rgba(143,68,32,.12)] bg-white px-3 py-[7px] font-body text-sm text-ink focus:border-orange focus:outline focus:outline-2 focus:outline-offset-2 focus:outline-orange';
  return (
    <div
      className="flex flex-wrap items-center gap-2 font-body text-[13px] text-muted"
      role="group"
      aria-label="Custom date range"
    >
      <label htmlFor={`${id}-start`} className="sr-only">
        From
      </label>
      <input
        id={`${id}-start`}
        type="date"
        className={input}
        value={value.start ?? ''}
        max={value.end}
        onChange={(e) => onChange({ ...value, start: e.target.value || undefined })}
      />
      <span aria-hidden="true">–</span>
      <label htmlFor={`${id}-end`} className="sr-only">
        To
      </label>
      <input
        id={`${id}-end`}
        type="date"
        className={input}
        value={value.end ?? ''}
        min={value.start}
        onChange={(e) => onChange({ ...value, end: e.target.value || undefined })}
      />
      {timeZoneLabel && <span className="text-xs">({timeZoneLabel})</span>}
    </div>
  );
}
